import type { MouseEvent } from 'react'
import { useEffect, useRef, useState } from 'react'

import { notEmpty } from 'utils/notEmpty'

import {
  clampCoordinates,
  createSimulatorArea,
  getOppositePoint,
  getPercentageCoordinate,
  updateAreaSize,
} from '../../utils/getCoordinates'

const MIN_COORDINATE = 0
const MAX_COORDINATE = 100

export type ResizeDirection = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

type ResizeItem = {
  id: string
  x: number
  y: number
  width: number
  height: number
}

interface IResizeProps<Item extends ResizeItem = ResizeItem> {
  boxNode: HTMLDivElement | HTMLImageElement | null
  list: Item[]
  createOrUpdate: (item: Item) => void
  enabledDraw: boolean
  setActiveId: (id: string | null) => void
}

const MIN_CREATE_SIZE = 5

export const useBoxResize = <Item extends ResizeItem = ResizeItem>({
  enabledDraw,
  boxNode,
  setActiveId,
  list,
  createOrUpdate,
}: IResizeProps<Item>) => {
  const startPoint = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const [editArea, setEditArea_raw] = useState<Item | null>(null)
  const editAreaRef = useRef<Item | null>(null)
  const setEditArea = (area: Item | null) => {
    editAreaRef.current = area
    setEditArea_raw(area)
  }
  const activeListeners = useRef<Record<string, (e: Event) => void>>({})
  const onDraw = (e: MouseEvent<HTMLDivElement>) => {
    if (editAreaRef.current || e.button !== 0) {
      return
    }
    const boxRect = boxNode?.getBoundingClientRect()
    if (!enabledDraw || !boxRect) {
      return
    }
    setActiveId(null)

    const { currentX, currentY } = getPercentageCoordinate(e.clientX, e.clientY, boxRect)

    const area = createSimulatorArea(currentX, currentY) as Item
    startPoint.current = { x: area.x, y: area.y }
    startMoveHandle(area, { isDraw: true })
  }

  const stopMove = () => {
    setEditArea(null)
    if (activeListeners.current.mousemove) {
      document.removeEventListener('mousemove', activeListeners.current.mousemove, true)
    }
    if (activeListeners.current.mouseup) {
      document.removeEventListener('mouseup', activeListeners.current.mouseup, true)
    }
  }

  const startMoveHandle = (area: Item, params?: { isDrag?: boolean; isDraw?: boolean }) => {
    if (!params?.isDraw) {
      setEditArea(area)
    }
    const onMove = onMouseMove(area, params)
    document.addEventListener('mousemove', onMove, true)
    document.addEventListener('mouseup', onMouseUp, true)
    activeListeners.current = {
      mousemove: onMove,
      mouseup: onMouseUp,
    }
  }
  const onMouseMove =
    (area: Item, params?: { isDrag?: boolean; isDraw?: boolean }) => (event: Event) => {
      const e = event as unknown as MouseEvent
      const imgRect = boxNode?.getBoundingClientRect()
      e.stopPropagation()
      e.preventDefault()
      if (!imgRect) {
        return
      }

      const { currentX, currentY } = getPercentageCoordinate(e.clientX, e.clientY, imgRect)
      const clampedCoords = clampCoordinates(
        currentX,
        currentY,
        0,
        0,
        MIN_COORDINATE,
        MAX_COORDINATE,
      )
      if (params?.isDrag) {
        const draggingArea = area

        const newX = draggingArea.x + (clampedCoords.x - startPoint.current.x)
        const newY = draggingArea.y + (clampedCoords.y - startPoint.current.y)

        const newCoordinates = clampCoordinates(
          newX,
          newY,
          draggingArea.width,
          draggingArea.height,
          MIN_COORDINATE,
          MAX_COORDINATE,
        )
        setEditArea({ ...area, ...newCoordinates })
      } else if (editAreaRef.current) {
        const updatedArea = updateAreaSize(startPoint.current, clampedCoords)
        setEditArea({ ...area, ...updatedArea })
      } else if (params?.isDraw) {
        const updatedArea = updateAreaSize(startPoint.current, clampedCoords)
        const nodeBox = boxNode?.getBoundingClientRect()
        const realWidth = updatedArea.width * (nodeBox?.width || 0)
        const realHeight = updatedArea.height * (nodeBox?.height || 0)
        const tooSmall = realWidth < MIN_CREATE_SIZE || realHeight < MIN_CREATE_SIZE
        if (!tooSmall) {
          setEditArea({ ...area, ...updatedArea })
        }
      }
    }

  const onMouseUp = () => {
    if (editAreaRef.current) {
      setActiveId(editAreaRef.current.id)
      createOrUpdate(editAreaRef.current)
    }
    stopMove()
  }

  const onDrag = (e: MouseEvent<HTMLDivElement>, area: Item) => {
    if (editAreaRef.current) {
      return
    }
    e.stopPropagation()
    const imgRect = boxNode?.getBoundingClientRect()

    setActiveId(area.id)

    if (!imgRect) {
      return
    }
    const { currentX, currentY } = getPercentageCoordinate(e.clientX, e.clientY, imgRect)
    startPoint.current = { x: currentX, y: currentY }

    startMoveHandle(area, { isDrag: true })
  }

  const onResize = (e: MouseEvent<HTMLDivElement>, area: Item, direction: ResizeDirection) => {
    e.stopPropagation()
    setActiveId(area.id)
    startPoint.current = getOppositePoint(area, direction)
    startMoveHandle(area)
  }
  useEffect(() => {
    return () => {
      stopMove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    onDrag,
    onResize,
    onDraw,
    items: [...list.filter((a) => a.id !== editArea?.id), editArea].filter(notEmpty),
  }
}
